/**
 * DesignRouteEditor
 *
 * Full-page client editor surface for a single Space design.
 *
 * Primary responsibilities:
 * - Load one design record from the Space designs API
 * - Render the shared design detail/editor experience
 * - Persist autosaved and collaborative design scene updates
 *
 * Dependencies:
 * - Space design detail view
 * - Shared autosave and collaboration hooks
 */

"use client"

import {
  startTransition,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import dynamic from "next/dynamic"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import {
  ArrowLeft,
  Clock3,
  ImagePlus,
  Layers3,
  Lock,
  Palette,
  Sparkles,
  Users,
  Wifi,
  WifiOff
} from "lucide-react"
import { toast } from "sonner"
import { authenticatedFetch } from "@/lib/auth/authenticated-fetch"
import { DesignDetailView } from "@/components/spaces/items/design-detail-view"
import {
  countDesignElements,
  type DesignDocument,
  normalizeDesignDocument
} from "@/components/spaces/designs/design-runtime"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { AutosaveStatus } from "@/components/ui/autosave-status"
import { useAutosave } from "@/hooks/use-autosave"
import { useMobileCreativeEditorBridge } from "@/hooks/use-mobile-creative-editor-bridge"
import { useSpaceDesignCollaboration } from "@/hooks/use-space-design-collaboration"
import { ImportDesignImageDialog } from "@/components/spaces/designs/import-design-image-dialog"
import { openSpaceConversation } from "@/lib/spaces/open-space-conversation"
import {
  spaceDesignDetailQueryOptions,
  spacesQueryKeys
} from "@/lib/spaces/queries"
import type { DesignDocumentAdapter } from "@/lib/spaces/design-collaboration/types"
import { cn } from "@/lib/utils"
import type { Json } from "@/supabase/types"
import { useSplitPaneNavigation } from "@/components/splitview/split-pane-context"
import type {
  PolotnoVendorRenderer,
  PolotnoVendorRendererProps
} from "@/components/spaces/designs/polotno-runtime-adapter"

const DynamicPolotnoDesignRenderer = dynamic<PolotnoVendorRendererProps>(
  () =>
    import("@/components/spaces/designs/polotno-design-renderer").then(
      module => module.PolotnoDesignRenderer
    ),
  {
    ssr: false,
    loading: () => (
      <div className="text-muted-foreground flex size-full items-center justify-center text-sm">
        Loading design canvas...
      </div>
    )
  }
)

const renderPolotnoDesign: PolotnoVendorRenderer = props => {
  return <DynamicPolotnoDesignRenderer {...props} />
}

interface DesignRouteEditorProps {
  spaceId: string
  designId: string
}

type SpaceDesign = {
  id: string
  space_id: string
  title?: string | null
  description?: string | null
  design_data?: unknown
  sharing?: string | null
  locked?: boolean | null
  created_at?: string
  updated_at?: string | null
}

type SpaceDesignDetailPayload =
  | ({ design?: SpaceDesign } & Partial<SpaceDesign>)
  | SpaceDesign

type DesignDraft = {
  title: string
  description: string | null
  document: DesignDocument
}

type AutosaveDesignDraft = {
  changeId: number
  draft: DesignDraft
}

const createEmptyDraft = (): DesignDraft => {
  const document = normalizeDesignDocument(null, {
    title: "Untitled Design",
    description: null
  })

  return {
    title: document.title,
    description: document.description,
    document
  }
}

const createDraftFromDesign = (design: SpaceDesign): DesignDraft => {
  const document = normalizeDesignDocument(design.design_data, {
    id: design.id,
    title: design.title || "Untitled Design",
    description: design.description || null
  })

  return {
    title: document.title,
    description: document.description,
    document
  }
}

const mergeDesignWithDraft = (
  design: SpaceDesign,
  draft: DesignDraft
): SpaceDesign => {
  return {
    ...design,
    title: draft.title,
    description: draft.description,
    design_data: draft.document
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null
}

const resolveDesign = (payload: SpaceDesignDetailPayload | null) => {
  if (!payload || !isRecord(payload)) {
    return null
  }

  if (isRecord(payload.design) && typeof payload.design.id === "string") {
    return payload.design as SpaceDesign
  }

  return typeof payload.id === "string"
    ? (payload as SpaceDesign)
    : null
}

const formatTimestamp = (value: string | Date | null | undefined) => {
  if (!value) {
    return "Not saved yet"
  }

  const date = value instanceof Date ? value : new Date(value)

  if (Number.isNaN(date.getTime())) {
    return "Not saved yet"
  }

  return date.toLocaleString()
}

const collaborationToneByStatus = {
  idle:
    "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-800 dark:bg-slate-950/50 dark:text-slate-300",
  bootstrapping:
    "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/70 dark:bg-amber-950/40 dark:text-amber-300",
  connecting:
    "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/70 dark:bg-amber-950/40 dark:text-amber-300",
  connected:
    "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300",
  error:
    "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/70 dark:bg-rose-950/40 dark:text-rose-300",
  closed:
    "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-800 dark:bg-slate-950/50 dark:text-slate-300"
} as const

const getCollaborationLabel = (status: ReturnType<
  typeof useSpaceDesignCollaboration
>["status"]) => {
  switch (status) {
    case "bootstrapping":
      return "Preparing room"
    case "connecting":
      return "Connecting"
    case "connected":
      return "Live"
    case "error":
      return "Connection issue"
    case "closed":
      return "Closed"
    case "idle":
    default:
      return "Offline"
  }
}

const getParticipantLabel = (participant: {
  availability: string
  displayName: string | null
  userId: string
}) => {
  return participant.displayName?.trim() || "User"
}

function DesignRouteSkeleton() {
  return (
    <div className="bg-background flex size-full min-h-0 flex-col">
      <div className="border-b px-4 py-3 sm:px-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Skeleton className="h-9 w-32" />
            <div className="space-y-2">
              <Skeleton className="h-3 w-28" />
              <Skeleton className="h-5 w-56" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-9 w-24" />
          </div>
        </div>
      </div>

      <div className="grid min-h-0 flex-1 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-h-0 border-r">
          <div className="border-b p-4 sm:px-6">
            <div className="space-y-3">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-16 w-full max-w-2xl" />
            </div>
          </div>

          <div className="min-h-0 p-4">
            <Skeleton className="size-full min-h-[520px] rounded-3xl" />
          </div>
        </div>

        <div className="space-y-4 p-4 sm:p-5">
          <Skeleton className="h-36 w-full rounded-3xl" />
          <Skeleton className="h-48 w-full rounded-3xl" />
          <Skeleton className="h-36 w-full rounded-3xl" />
        </div>
      </div>
    </div>
  )
}

export function DesignRouteEditor({
  spaceId,
  designId
}: DesignRouteEditorProps) {
  const { navigateToHref } = useSplitPaneNavigation()
  const queryClient = useQueryClient()
  const designRef = useRef<SpaceDesign | null>(null)
  const draftRef = useRef<DesignDraft>(createEmptyDraft())
  const draftChangeIdRef = useRef(1)
  const draftVersionRef = useRef(1)
  const lastHydratedDesignKeyRef = useRef<string | null>(null)
  const lastSavedDraftChangeIdRef = useRef(1)
  const lastBroadcastDraftChangeIdRef = useRef(1)
  const lastResyncRequestKeyRef = useRef("")
  const preferLocalDraftOnReconnectRef = useRef(false)
  const skipNextBroadcastRef = useRef(false)

  const [design, setDesign] = useState<SpaceDesign | null>(null)
  const [editorHydrationDesign, setEditorHydrationDesign] =
    useState<SpaceDesign | null>(null)
  const [draft, setDraft] = useState<DesignDraft>(() => createEmptyDraft())
  const [autosaveResetToken, setAutosaveResetToken] = useState(0)
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false)
  const [isImportingImage, setIsImportingImage] = useState(false)
  const [isOpeningThread, setIsOpeningThread] = useState(false)
  const designQuery = useQuery({
    ...spaceDesignDetailQueryOptions(spaceId, designId),
    enabled: Boolean(spaceId && designId)
  })
  const blockingError =
    !design
      ? designQuery.error instanceof Error
        ? designQuery.error.message
        : !designQuery.isLoading && designQuery.data === null
          ? "Design not found"
          : null
      : null
  const isLoading = designQuery.isLoading && !design

  useEffect(() => {
    designRef.current = design
  }, [design])

  useEffect(() => {
    draftRef.current = draft
  }, [draft])

  const applyIncomingDocument = useCallback(
    (documentValue: unknown, version?: number | null) => {
      const currentDesign = designRef.current
      const nextDocument = normalizeDesignDocument(documentValue, {
        id: designId,
        title:
          currentDesign?.title ||
          draftRef.current.title ||
          "Untitled Design",
        description:
          currentDesign?.description ??
          draftRef.current.description ??
          null
      })

      const nextDraft: DesignDraft = {
        title: nextDocument.title,
        description: nextDocument.description,
        document: nextDocument
      }
      const hasLocalDraftAhead =
        draftChangeIdRef.current !== lastBroadcastDraftChangeIdRef.current
      const hasUnsavedLocalDraft =
        draftChangeIdRef.current !== lastSavedDraftChangeIdRef.current

      const nextDesign = currentDesign
        ? mergeDesignWithDraft(currentDesign, nextDraft)
        : null

      if (preferLocalDraftOnReconnectRef.current && hasLocalDraftAhead) {
        return
      }

      if (hasLocalDraftAhead) {
        return
      }

      if (hasUnsavedLocalDraft) {
        return
      }

      if (
        typeof version === "number" &&
        version < draftVersionRef.current
      ) {
        return
      }

      const nextDraftChangeId = draftChangeIdRef.current + 1
      draftChangeIdRef.current = nextDraftChangeId
      draftVersionRef.current = Math.max(
        draftVersionRef.current,
        version ?? draftVersionRef.current
      ) + 1
      lastBroadcastDraftChangeIdRef.current = nextDraftChangeId
      preferLocalDraftOnReconnectRef.current = false

      skipNextBroadcastRef.current = true
      draftRef.current = nextDraft

      startTransition(() => {
        setDraft(nextDraft)
      })

      if (nextDesign) {
        designRef.current = nextDesign
        setDesign(nextDesign)
        setEditorHydrationDesign(nextDesign)
      }
    },
    [designId]
  )

  const documentAdapter = useMemo<DesignDocumentAdapter | null>(() => {
    if (!designId) {
      return null
    }

    return {
      applyRemoteOperation: (operation) => {
        if (operation.type !== "document.replace") {
          return
        }

        applyIncomingDocument(operation.payload, operation.version ?? null)
      },
      applyRemoteSnapshot: (snapshot) => {
        applyIncomingDocument(snapshot.snapshot, snapshot.version ?? null)
      },
      captureSnapshot: () => {
        return draftRef.current.document as unknown as Json
      },
      getDebugLabel: () => `space-design:${spaceId}:${designId}`,
      getVersion: () => draftVersionRef.current
    }
  }, [applyIncomingDocument, designId, spaceId])

  const collaboration = useSpaceDesignCollaboration({
    designId,
    documentAdapter,
    enabled: Boolean(design),
    spaceId
  })

  const isPermissionReadOnly =
    Boolean(design) && collaboration.session?.permissions.canEdit === false
  const isReadOnly = Boolean(design?.locked) || isPermissionReadOnly
  const mobileBridge = useMobileCreativeEditorBridge({
    itemId: designId,
    itemType: "design",
    readOnly: isReadOnly,
    spaceId
  })
  const draftChangeId = draftChangeIdRef.current
  const autosaveValue = useMemo<AutosaveDesignDraft>(() => {
    return {
      changeId: draftChangeId,
      draft
    }
  }, [draft, draftChangeId])

  useEffect(() => {
    if (!blockingError) {
      return
    }

    mobileBridge.postError({
      code: "design-unavailable",
      message: blockingError,
      recoverable: true
    })
  }, [blockingError, mobileBridge.postError])

  const broadcastCurrentDesignSnapshot = useCallback(async () => {
    const currentDraftChangeId = draftChangeIdRef.current

    if (currentDraftChangeId === lastBroadcastDraftChangeIdRef.current) {
      preferLocalDraftOnReconnectRef.current = false
      return
    }

    const nextVersion = draftVersionRef.current + 1
    draftVersionRef.current = nextVersion

    try {
      await collaboration.broadcastSnapshot(
        draftRef.current.document as unknown as Json,
        {
          version: nextVersion
        }
      )

      lastBroadcastDraftChangeIdRef.current = currentDraftChangeId
      preferLocalDraftOnReconnectRef.current = false
    } catch (error) {
      draftVersionRef.current = Math.max(1, draftVersionRef.current - 1)
      throw error
    }
  }, [collaboration.broadcastSnapshot])

  useEffect(() => {
    if (collaboration.isConnected) {
      return
    }

    if (draftChangeId !== lastBroadcastDraftChangeIdRef.current) {
      preferLocalDraftOnReconnectRef.current = true
    }
  }, [collaboration.isConnected, draftChangeId])

  useEffect(() => {
    if (designQuery.data === undefined) {
      return
    }

    if (!designQuery.data) {
      const emptyDraft = createEmptyDraft()

      designRef.current = null
      draftRef.current = emptyDraft
      draftChangeIdRef.current = 1
      draftVersionRef.current = 1
      lastHydratedDesignKeyRef.current = null
      lastSavedDraftChangeIdRef.current = 1
      lastBroadcastDraftChangeIdRef.current = 1
      lastResyncRequestKeyRef.current = ""
      preferLocalDraftOnReconnectRef.current = false
      skipNextBroadcastRef.current = false

      setDesign(null)
      setEditorHydrationDesign(null)
      setDraft(emptyDraft)
      setAutosaveResetToken(currentToken => currentToken + 1)
      return
    }

    const incomingDesign = designQuery.data as SpaceDesign
    const hydrationKey = [
      incomingDesign.id,
      incomingDesign.updated_at || incomingDesign.created_at || "initial"
    ].join(":")

    if (lastHydratedDesignKeyRef.current === hydrationKey) {
      return
    }

    const nextDraft = createDraftFromDesign(incomingDesign)
    const hydratedDesign = mergeDesignWithDraft(incomingDesign, nextDraft)
    const hasUnsavedLocalDraft =
      draftChangeIdRef.current !== lastSavedDraftChangeIdRef.current

    lastHydratedDesignKeyRef.current = hydrationKey

    if (hasUnsavedLocalDraft) {
      designRef.current = hydratedDesign
      setDesign(hydratedDesign)
      return
    }

    designRef.current = hydratedDesign
    draftRef.current = nextDraft
    draftChangeIdRef.current = 1
    draftVersionRef.current = 1
    lastSavedDraftChangeIdRef.current = 1
    lastBroadcastDraftChangeIdRef.current = 1
    lastResyncRequestKeyRef.current = ""
    preferLocalDraftOnReconnectRef.current = false
    skipNextBroadcastRef.current = false

    setDesign(hydratedDesign)
    setEditorHydrationDesign(hydratedDesign)
    setDraft(nextDraft)
    setAutosaveResetToken(currentToken => currentToken + 1)
  }, [designQuery.data])

  useEffect(() => {
    if (!(designQuery.error instanceof Error)) {
      return
    }

    toast.error(designQuery.error.message)
  }, [designQuery.error])

  const autosave = useAutosave({
    value: autosaveValue,
    enabled: Boolean(design) && !isReadOnly,
    delay: 900,
    resetKey: `${design?.id ?? "none"}:${autosaveResetToken}`,
    isEqual: (left, right) => left.changeId === right.changeId,
    onSave: async ({ changeId, draft: currentDraft }) => {
      const currentDesign = designRef.current
      const savedDraftChangeId = changeId

      if (!currentDesign) {
        return
      }

      const response = await authenticatedFetch(
        `/api/spaces/${spaceId}/designs/${currentDesign.id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            title: currentDraft.title,
            description: currentDraft.description,
            design_data: currentDraft.document
          })
        }
      )

      if (!response.ok) {
        throw new Error(await response.text())
      }

      const payload = (await response
        .json()
        .catch(() => null)) as SpaceDesignDetailPayload | null
      const savedDesign = resolveDesign(payload)
      const latestDraft = draftRef.current
      const hasNewerLocalDraft =
        draftChangeIdRef.current !== savedDraftChangeId

      if (!savedDesign) {
        lastSavedDraftChangeIdRef.current = savedDraftChangeId

        const liveDesign = mergeDesignWithDraft(
          {
            ...currentDesign,
            updated_at: new Date().toISOString()
          },
          hasNewerLocalDraft ? latestDraft : currentDraft
        )

        designRef.current = liveDesign
        setDesign(liveDesign)

        if (!hasNewerLocalDraft) {
          setEditorHydrationDesign(liveDesign)
        }

        return
      }

      const persistedDesign = mergeDesignWithDraft(savedDesign, currentDraft)
      const liveDesign = mergeDesignWithDraft(
        savedDesign,
        hasNewerLocalDraft ? latestDraft : currentDraft
      )

      lastSavedDraftChangeIdRef.current = savedDraftChangeId
      queryClient.setQueryData(
        spacesQueryKeys.designDetail(spaceId, currentDesign.id),
        persistedDesign
      )
      queryClient.setQueryData(
        spacesQueryKeys.designs(spaceId),
        (currentDesigns: SpaceDesign[] | undefined) =>
          (currentDesigns ?? []).map(existingDesign =>
            existingDesign.id === persistedDesign.id
              ? {
                  ...existingDesign,
                  ...persistedDesign
                }
              : existingDesign
          )
      )
      designRef.current = liveDesign
      setDesign(liveDesign)

      if (!hasNewerLocalDraft) {
        setEditorHydrationDesign(persistedDesign)
      }
    },
    onSaved: () => {
      mobileBridge.postSaved({
        updatedAt: new Date().toISOString()
      })

      if (!collaboration.isConnected || !collaboration.canEdit || isReadOnly) {
        return
      }

      void broadcastCurrentDesignSnapshot().catch(() => {})
    },
    onError: (saveError) => {
      const message =
        saveError instanceof Error
          ? saveError.message
          : "Failed to save design"

      toast.error(message)
      mobileBridge.postError({
        code: "design-save-failed",
        message
      })
    }
  })

  useEffect(() => {
    if (!design || isReadOnly) {
      return
    }

    const flushPendingSave = () => {
      void autosave.flush()
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flushPendingSave()
      }
    }

    window.addEventListener("pagehide", flushPendingSave)
    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      window.removeEventListener("pagehide", flushPendingSave)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [autosave, design, isReadOnly])

  useEffect(() => {
    if (!design || !collaboration.isConnected) {
      return
    }

    void collaboration.updateAwareness({
      activeTool: isReadOnly ? "view" : "design-editor",
      metadata: {
        mode: isReadOnly ? "view" : "edit",
        title: draft.title
      },
      viewport: {
        x: 0,
        y: 0,
        zoom: 1,
        pageId: draft.document.activePageId
      }
    })
  }, [
    collaboration.isConnected,
    collaboration.updateAwareness,
    design,
    draft.document.activePageId,
    draft.title,
    isReadOnly
  ])

  useEffect(() => {
    if (!design || !collaboration.isConnected || !collaboration.connectedAt) {
      return
    }

    const requestKey = `${design.id}:${collaboration.connectedAt}`

    if (lastResyncRequestKeyRef.current === requestKey) {
      return
    }

    lastResyncRequestKeyRef.current = requestKey

    if (
      collaboration.canEdit &&
      !isReadOnly &&
      draftChangeId !== lastBroadcastDraftChangeIdRef.current
    ) {
      void broadcastCurrentDesignSnapshot().catch(() => {})
      return
    }

    preferLocalDraftOnReconnectRef.current = false
    void collaboration.requestResync("route-connected")
  }, [
    broadcastCurrentDesignSnapshot,
    collaboration.canEdit,
    collaboration.connectedAt,
    collaboration.isConnected,
    collaboration.requestResync,
    design,
    draftChangeId,
    isReadOnly
  ])

  useEffect(() => {
    if (!design || isReadOnly || !collaboration.isConnected || !collaboration.canEdit) {
      return
    }

    if (draftChangeId === lastBroadcastDraftChangeIdRef.current) {
      return
    }

    if (skipNextBroadcastRef.current) {
      skipNextBroadcastRef.current = false
      lastBroadcastDraftChangeIdRef.current = draftChangeIdRef.current
      return
    }

    const timeoutId = window.setTimeout(() => {
      const currentDraftChangeId = draftChangeIdRef.current

      if (currentDraftChangeId === lastBroadcastDraftChangeIdRef.current) {
        preferLocalDraftOnReconnectRef.current = false
        return
      }

      const nextVersion = draftVersionRef.current + 1
      draftVersionRef.current = nextVersion

      void collaboration
        .broadcastOperation({
          payload: draftRef.current.document as unknown as Json,
          type: "document.replace",
          version: nextVersion
        })
        .then(() => {
          lastBroadcastDraftChangeIdRef.current = currentDraftChangeId
          preferLocalDraftOnReconnectRef.current = false
        })
        .catch((broadcastError) => {
          draftVersionRef.current = Math.max(1, draftVersionRef.current - 1)
          console.error("Design collaboration broadcast failed:", broadcastError)
        })
    }, 180)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [
    collaboration.broadcastOperation,
    collaboration.canEdit,
    collaboration.isConnected,
    design,
    draftChangeId,
    isReadOnly
  ])

  const handleNavigate = async (href: string) => {
    if (!design || isReadOnly) {
      navigateToHref(href)
      return
    }

    const didSave = await autosave.flush()

    if (!didSave && autosave.hasPendingChanges) {
      return
    }

    navigateToHref(href)
  }

  const handleOpenLosiThread = async () => {
    if (!design || isOpeningThread) {
      return
    }

    setIsOpeningThread(true)

    try {
      if (!isReadOnly) {
        const didSave = await autosave.flush()

        if (!didSave && autosave.hasPendingChanges) {
          return
        }
      }

      const designTitle = draft.title || design.title || "Untitled Design"
      const { href } = await openSpaceConversation({
        autoIncludeAttachments: true,
        autoIncludeHierarchy: true,
        autoIncludeNotes: true,
        autoIncludeTasks: true,
        contextType: "space",
        description: `Shared Losi thread for design ${designTitle}`,
        name: `${designTitle} Design Thread`,
        preferExisting: false,
        space: {
          id: spaceId,
          name: designTitle
        },
        userId: "space-design-route"
      })

      navigateToHref(href)
    } catch (openError) {
      const message =
        openError instanceof Error
          ? openError.message
          : "Failed to open shared Losi thread"

      toast.error(message)
    } finally {
      setIsOpeningThread(false)
    }
  }

  const handleImportDesignImage = async (payload: {
    title: string
    description: string | null
    file: File
  }) => {
    if (!design || isImportingImage || isReadOnly) {
      return
    }

    setIsImportingImage(true)

    try {
      if (!isReadOnly) {
        const didSave = await autosave.flush()

        if (!didSave && autosave.hasPendingChanges) {
          return
        }
      }

      const formData = new FormData()
      formData.append("file", payload.file)
      formData.append("title", payload.title)
      formData.append("designId", design.id)

      if (payload.description) {
        formData.append("description", payload.description)
      }

      const response = await authenticatedFetch(`/api/spaces/${spaceId}/designs/import`, {
        method: "POST",
        body: formData
      })
      const responseText = await response.text()

      if (!response.ok) {
        let errorMessage = responseText || "Failed to replace design from image"

        try {
          const parsedError = JSON.parse(responseText) as { error?: string }
          errorMessage = parsedError.error || errorMessage
        } catch {}

        throw new Error(errorMessage)
      }

      const responsePayload = JSON.parse(responseText) as {
        design?: SpaceDesign
      }
      const nextDesign = responsePayload.design

      if (!nextDesign?.id) {
        throw new Error("Imported design response missing id")
      }

      queryClient.setQueryData(
        spacesQueryKeys.designDetail(spaceId, nextDesign.id),
        nextDesign
      )
      queryClient.setQueryData(
        spacesQueryKeys.designs(spaceId),
        (currentDesigns: SpaceDesign[] | undefined) =>
          (currentDesigns ?? []).map((currentDesign) =>
            currentDesign.id === nextDesign.id ? nextDesign : currentDesign
          )
      )

      toast.success("Design rebuilt from image")
    } catch (importError) {
      const message =
        importError instanceof Error
          ? importError.message
          : "Failed to replace design from image"

      toast.error(message)
    } finally {
      setIsImportingImage(false)
    }
  }

  const handleDocumentChange = (nextDocument: DesignDocument) => {
    if (isReadOnly) {
      return
    }

    const nextDraft: DesignDraft = {
      title: nextDocument.title,
      description: nextDocument.description,
      document: nextDocument
    }

    draftVersionRef.current += 1
    draftChangeIdRef.current += 1
    draftRef.current = nextDraft

    startTransition(() => {
      setDraft(nextDraft)
    })
  }

  const deferredDocument = useDeferredValue(draft.document)
  const pageCount = deferredDocument.pages.length
  const layerCount = useMemo(() => {
    return countDesignElements(deferredDocument)
  }, [deferredDocument])
  const saveTimestamp = autosave.lastSavedAt || design?.updated_at || design?.created_at

  if (isLoading) {
    return <DesignRouteSkeleton />
  }

  if (!design || blockingError) {
    return (
      <div className="bg-background flex size-full flex-col items-center justify-center gap-4 p-6 text-center">
        <div className="space-y-3">
          <div className="mx-auto flex size-14 items-center justify-center rounded-3xl bg-pink-50 text-pink-600 dark:bg-pink-950/40 dark:text-pink-300">
            <Palette className="size-6" />
          </div>
          <div className="space-y-1">
            <h1 className="text-xl font-semibold">Design unavailable</h1>
            <p className="text-muted-foreground max-w-md text-sm">
              {blockingError || "This design could not be loaded for the current space."}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => void handleNavigate(`/spaces/${spaceId}/designs`)}
          >
            <ArrowLeft className="mr-2 size-4" />
            Back to Designs
          </Button>
          <Button variant="outline" onClick={() => void designQuery.refetch()}>
            Retry
          </Button>
          <Button onClick={() => void handleNavigate(`/spaces/${spaceId}`)}>
            Back to Space
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-background flex size-full min-h-0 flex-col">
      <div className="border-b px-4 py-3 sm:px-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              className="px-0"
              onClick={() => void handleNavigate(`/spaces/${spaceId}/designs`)}
            >
              <ArrowLeft className="mr-2 size-4" />
              Back to Designs
            </Button>

            <div className="min-w-0">
              <p className="text-muted-foreground text-xs font-medium uppercase tracking-[0.18em]">
                Space Design
              </p>
              <p className="truncate text-sm font-medium">
                {draft.title || design.title || "Untitled Design"}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium">
              <Palette className="size-3.5" />
              Design
            </span>

            <span className="inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium">
              <Layers3 className="size-3.5" />
              {pageCount} {pageCount === 1 ? "page" : "pages"}
            </span>

            <span className="inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium">
              <Users className="size-3.5" />
              {collaboration.participants.length} active
            </span>

            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium",
                collaborationToneByStatus[collaboration.status]
              )}
            >
              {collaboration.isConnected ? (
                <Wifi className="size-3.5" />
              ) : (
                <WifiOff className="size-3.5" />
              )}
              {getCollaborationLabel(collaboration.status)}
            </span>

            {isReadOnly ? (
              <span className="text-muted-foreground inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium">
                <Lock className="size-3.5" />
                Read-only
              </span>
            ) : (
              <>
                <AutosaveStatus
                  status={autosave.status}
                  lastSavedAt={autosave.lastSavedAt}
                  className="rounded-full border px-3 py-1"
                  errorLabel="Could not save design"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsImportDialogOpen(true)}
                  disabled={isImportingImage}
                >
                  <ImagePlus className="mr-2 size-4" />
                  {isImportingImage ? "Rebuilding..." : "Import Image"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void autosave.flush()}
                  disabled={autosave.isSaving}
                >
                  Save now
                </Button>
              </>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => void handleNavigate(`/spaces/${spaceId}`)}
            >
              Space Overview
            </Button>
          </div>
        </div>
      </div>

      <div className="grid min-h-0 flex-1 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-h-0 border-b lg:border-b-0 lg:border-r">
          <DesignDetailView
            design={editorHydrationDesign || design}
            className="h-full"
            readOnly={isReadOnly}
            onDocumentChange={handleDocumentChange}
            preferredRuntime="polotno"
            renderPolotno={renderPolotnoDesign}
          />
        </div>

        <aside className="bg-muted/20 min-h-0 overflow-auto p-4 sm:p-5">
          <div className="space-y-4">
            <section className="bg-background rounded-3xl border p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <p className="text-sm font-semibold">Live Collaboration</p>
                  <p className="text-muted-foreground text-xs">
                    Presence, shared updates, and on-demand resync for this
                    canvas.
                  </p>
                </div>
                <span
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium",
                    collaborationToneByStatus[collaboration.status]
                  )}
                >
                  {getCollaborationLabel(collaboration.status)}
                </span>
              </div>

              {collaboration.lastError ? (
                <p className="mt-3 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:border-rose-900/70 dark:bg-rose-950/40 dark:text-rose-300">
                  {collaboration.lastError}
                </p>
              ) : null}

              <div className="mt-4 space-y-2">
                {collaboration.participants.length > 0 ? (
                  collaboration.participants.map((participant) => (
                    <div
                      key={participant.sessionId}
                      className="flex items-center justify-between gap-3 rounded-2xl border px-3 py-2"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <span
                          className="size-2.5 rounded-full"
                          style={{ backgroundColor: participant.color }}
                        />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">
                            {getParticipantLabel(participant)}
                          </p>
                          <p className="text-muted-foreground truncate text-xs">
                            {participant.role} · {participant.availability}
                          </p>
                        </div>
                      </div>

                      {participant.isSelf ? (
                        <span className="rounded-full bg-pink-50 px-2 py-1 text-[11px] font-medium text-pink-700 dark:bg-pink-950/40 dark:text-pink-300">
                          You
                        </span>
                      ) : null}
                    </div>
                  ))
                ) : (
                  <div className="text-muted-foreground rounded-2xl border border-dashed px-3 py-4 text-center text-sm">
                    No other collaborators are in this design yet.
                  </div>
                )}
              </div>

              <div className="mt-4 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => void collaboration.requestResync("manual-panel")}
                  disabled={!collaboration.isConnected}
                >
                  Refresh from room
                </Button>
              </div>
            </section>

            <section className="bg-background rounded-3xl border p-4">
              <div className="space-y-1">
                <p className="text-sm font-semibold">Canvas Health</p>
                <p className="text-muted-foreground text-xs">
                  Structure, save state, and publishing metadata.
                </p>
              </div>

              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-muted-foreground">Layers</span>
                  <span className="font-medium">{layerCount}</span>
                </div>
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-muted-foreground">Sharing</span>
                  <span className="font-medium capitalize">
                    {design.sharing || "space"}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-muted-foreground">Save state</span>
                  <span className="font-medium capitalize">
                    {autosave.status}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-muted-foreground">Last saved</span>
                  <span className="text-right font-medium">
                    {formatTimestamp(saveTimestamp)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-muted-foreground">Unsaved changes</span>
                  <span className="font-medium">
                    {autosave.hasPendingChanges ? "Yes" : "No"}
                  </span>
                </div>
              </div>
            </section>

            <section className="bg-background rounded-3xl border p-4">
              <div className="space-y-1">
                <p className="text-sm font-semibold">Losi Workflow</p>
                <p className="text-muted-foreground text-xs">
                  Keep design work and AI collaboration in the same Space.
                </p>
              </div>

              <div className="mt-4 rounded-2xl border bg-pink-50/70 p-3 dark:border-pink-950/60 dark:bg-pink-950/20">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 rounded-full bg-pink-100 p-2 text-pink-700 dark:bg-pink-950/50 dark:text-pink-300">
                    <Sparkles className="size-4" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      Design tools are now part of the Spaces platform layer.
                    </p>
                    <p className="text-muted-foreground text-xs">
                      Use the Space overview and shared Losi threads to plan
                      revisions, generate copy, and trigger design mutations
                      against this canvas.
                    </p>
                  </div>
                </div>
              </div>

              <Button
                variant="outline"
                className="mt-4 w-full"
                onClick={() => setIsImportDialogOpen(true)}
                disabled={isImportingImage || isReadOnly}
              >
                <ImagePlus className="mr-2 size-4" />
                {isImportingImage ? "Rebuilding..." : "Replace from image"}
              </Button>

              <Button
                variant="outline"
                className="mt-2 w-full"
                onClick={() => void handleOpenLosiThread()}
                disabled={isOpeningThread}
              >
                {isOpeningThread ? "Opening thread..." : "Open shared Losi thread"}
              </Button>

              <div className="text-muted-foreground mt-4 flex items-center gap-2 text-xs">
                <Clock3 className="size-3.5" />
                {collaboration.connectedAt
                  ? `Room joined ${formatTimestamp(collaboration.connectedAt)}`
                  : "Room not connected yet"}
              </div>
            </section>
          </div>
        </aside>
      </div>

      <ImportDesignImageDialog
        isOpen={isImportDialogOpen}
        mode="replace"
        defaultTitle={draft.title || design.title || ""}
        defaultDescription={draft.description || design.description || ""}
        submitLabel="Replace With Import"
        onClose={() => setIsImportDialogOpen(false)}
        onImport={handleImportDesignImage}
      />
    </div>
  )
}
